import { useContext, useCallback, useMemo } from 'react';
import { ShopContext } from '../providers/ShopProvider';
import {
  formatMoney,
  formatMoneyCompact,
  extractCurrencySymbol,
  getCurrencyByCode,
  formatPercentage,
  formatNumber,
} from '../utils/currencyUtils';

/**
 * useCurrency Hook
 *
 * Provides currency formatting functions that use the shop's configured currency.
 * Automatically extracts currency settings from ShopContext.
 *
 * @returns {Object} Currency formatting utilities
 */
export function useCurrency() {
  const { shop } = useContext(ShopContext);

  // Extract currency settings from shop
  const currency = shop?.currency || 'USD';
  const moneyFormat = shop?.moneyFormat || '${{amount}}';
  const moneyWithCurrencyFormat = shop?.moneyWithCurrencyFormat || '${{amount}} USD';

  // Get currency symbol
  const currencySymbol = useMemo(() => {
    return extractCurrencySymbol(moneyFormat);
  }, [moneyFormat]);

  // Get currency info by code (fallback)
  const currencyInfo = useMemo(() => {
    return getCurrencyByCode(currency);
  }, [currency]);

  /**
   * Format amount using shop's money format
   */
  const format = useCallback((amount, options = {}) => {
    return formatMoney(amount, moneyFormat, options);
  }, [moneyFormat]);

  /**
   * Format amount with currency code (e.g., "$100.00 USD")
   */
  const formatWithCode = useCallback((amount, options = {}) => {
    return formatMoney(amount, moneyWithCurrencyFormat, options);
  }, [moneyWithCurrencyFormat]);

  /**
   * Format amount in compact form (K, M, B)
   */
  const formatCompact = useCallback((amount) => {
    return formatMoneyCompact(amount, currencySymbol);
  }, [currencySymbol]);

  /**
   * Format for chart axis labels (compact, no decimals)
   */
  const formatAxis = useCallback((amount) => {
    if (amount === 0) return `${currencySymbol}0`;
    return formatMoneyCompact(amount, currencySymbol);
  }, [currencySymbol]);

  /**
   * Format for tooltips (full precision)
   */
  const formatTooltip = useCallback((amount) => {
    return formatMoney(amount, moneyFormat, { showCents: true });
  }, [moneyFormat]);

  return {
    // Currency info
    currency,
    currencySymbol,
    moneyFormat,
    currencyInfo,

    // Formatting functions
    format,
    formatWithCode,
    formatCompact,
    formatAxis,
    formatTooltip,

    // Pass-through utilities
    formatPercentage,
    formatNumber,
  };
}

export default useCurrency;
